import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { getRepository } from '../data/repository';
import type { Profile, ProfileType } from '../types/vault';
import { config } from '../data/config';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { ModalDrawer } from '../components/ModalDrawer';
import { ProfileForm } from '../components/ProfileForm';

const { FiAlertCircle, FiRefreshCw, FiSearch, FiPlus } = FiIcons;

const profileTypeIcons: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  Individual: FiIcons.FiUser,
  Joint: FiIcons.FiUsers,
  Company: FiIcons.FiBriefcase,
  Business: FiIcons.FiBriefcase,
  SMSF: FiIcons.FiShield,
};

export function Profiles() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProfileType | ''>('');
  const [isAdding, setIsAdding] = useState(false);

  const role = getRepository().getCurrentUser()?.role || 'read_only';
  const canWrite = role === 'owner' || role === 'member';

  const loadProfiles = useCallback(() => {
    setLoading(true);
    setError(null);
    getRepository()
      .listProfiles()
      .then(setProfiles)
      .catch(err => setError(err.message || 'Unable to load profiles'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return profiles.filter(p => {
      if (p.archived) return false;
      if (typeFilter && p.profileType !== typeFilter) return false;
      if (!q) return true;
      return (
        p.displayName.toLowerCase().includes(q) ||
        (p.legalName?.toLowerCase().includes(q) ?? false) ||
        (p.aliases?.some(a => a.toLowerCase().includes(q)) ?? false)
      );
    });
  }, [profiles, search, typeFilter]);

  const handleAddProfile = async (profile: Profile) => {
    await getRepository().saveProfile(profile);
    setIsAdding(false);
    loadProfiles();
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-40" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-32 bg-slate-200 rounded-2xl" />
          <div className="h-32 bg-slate-200 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4 text-center">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-400" />
        <h2 className="text-lg font-semibold text-slate-900">Unable to Load Profiles</h2>
        <p className="text-slate-500 max-w-md">{error}</p>
        <button onClick={loadProfiles} className="inline-flex items-center px-4 py-2 bg-[#0B2138] text-white rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E]">
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">Entities &amp; Profiles</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">Manage personal, family, and business profiles.</p>
        </div>
        {canWrite && (
          <button onClick={() => setIsAdding(true)} className="inline-flex items-center px-4 py-2 bg-[#102D4C] text-white rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E] whitespace-nowrap">
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />Add Profile
          </button>
        )}
      </header>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, legal name, or alias..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as ProfileType | '')} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F766E]">
          <option value="">All Types</option>
          {config.profileTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="py-12 text-center border-2 border-dashed border-slate-200 rounded-2xl">
          <p className="text-slate-500">{profiles.filter(p => !p.archived).length === 0 ? 'No active profiles have been established yet.' : 'No profiles match your search.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {filtered.map(profile => (
            <Link key={profile.id} to={`/profiles/${profile.id}`} className="block bg-white p-5 rounded-2xl shadow-sm border border-slate-100 hover:border-[#B98937]/30 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0F766E]">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-[#102D4C]/5 rounded-full flex items-center justify-center text-[#102D4C] border border-[#102D4C]/10"><SafeIcon icon={profileTypeIcons[profile.profileType] || FiIcons.FiUser} className="w-5 h-5" /></div>
                <div className="ml-3 min-w-0">
                  <h3 className="text-base font-medium text-slate-900 truncate">{profile.displayName}</h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#0F766E]/10 text-[#0F766E] mt-0.5">{profile.profileType}</span>
                </div>
              </div>
              {profile.legalName && <p className="text-xs text-slate-400 mb-1 truncate">{profile.legalName}</p>}
              <p className="text-xs text-slate-400">Added {new Date(profile.created).toLocaleDateString()}</p>
            </Link>
          ))}
        </div>
      )}

      <ModalDrawer isOpen={isAdding} onClose={() => setIsAdding(false)} title="Add Profile">
        <ProfileForm onSave={handleAddProfile} onCancel={() => setIsAdding(false)} />
      </ModalDrawer>
    </div>
  );
}